import MetaContent from '@/Components/Common/Markdown/MetaContent'; //Meta content Includes reasoning tokens and deep research sources
import { ArrowDownTrayIcon, HeartIcon } from '@heroicons/react/24/solid';
import { Tooltip } from '@mui/material';
import useChatStore from 'Components/Chat/store';
import useHighlight from 'Components/Common/Markdown/hooks/useHighlight';
import useSmartMergeStore, { MERGE_STATES } from 'Features/SmartMerge/store';
import useIsMobile from 'Hooks/useIsMobile';
import useThemeStore from 'Theme/store';
import katex from 'katex'; // Import KaTeX to render mathematical equations
import 'katex/dist/katex.min.css'; // Import CSS styles for KaTeX
import { forwardRef, memo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { HighlightedText } from './HighlightedText';
import ImageMaskEditorModal from './ImageMaskEditorModal';
import { MemoizedReactMarkdown } from './Markdown';
import { MarkdownComponents } from './Markdown.components';
import { MemoizedOriginalPrompt } from './OriginalPrompt';

const ContentMessage = forwardRef(
  (
    {
      store,
      videos,
      images,
      onSend,
      message,
      loading,
      userType,
      messageId,
      capability,
      onOpenFull,
      optimizing,
      originalPrompt,
      onFavoriteFile,
      highlights = [],
      expanded = true,
      highlight = false,
      functionCapability,
      renderedFromShareChatPage,
      reasoning,
      annotations,
    },
    ref
  ) => {
    const isMobile = useIsMobile();
    const { theme } = useThemeStore();

    const newHighlights = useSmartMergeStore(
      (state) => state.highlights[messageId] || []
    );
    const setNewHighlights = useSmartMergeStore((state) => state.setHighlights);
    const messages = useSmartMergeStore((state) => state.messages);
    const mergeActive = useSmartMergeStore((state) => state.active);
    const selectedChat = useChatStore((state) => state.selectedChat);
    const mergeState = useSmartMergeStore((state) => state.activeTab);

    const [isOpen, setIsOpen] = useState(false);
    const [modalImage, setModalImage] = useState(null);
    const [maskEditorOpen, setMaskEditorOpen] = useState(false);
    const [maskEditorImage, setMaskEditorImage] = useState(null);

    const messageContainerRef = useRef(null);
    const [isTouching, setIsTouching] = useState(false);
    const [allHighlight, setAllHighlight] = useState(false);
    const [touchStartPos, setTouchStartPos] = useState(null);

    /**
     * Renders mathematical equations using KaTeX.
     * @param {string} equation - The equation to render.
     * @param {boolean} displayMode - Block mode (true) or inline mode (false).
     * @returns {string} - The rendered equation in HTML.
     */
    const renderEquation = (equation, displayMode = false) => {
      try {
        const className = displayMode ? 'latex-block' : 'latex-inline';
        return `<span class="${className}">${katex.renderToString(equation, {
          throwOnError: false,
          displayMode,
        })}</span>`;
      } catch (error) {
        console.error('KaTeX Error:', error);
        return `<span style="color:#cc0000">Error in equation: ${equation}</span>`;
      }
    };

    const getMessage = (message) => {
      if (typeof message == 'string') {
        return processing(message);
      }

      if (message.length === 1 && message[0].type === 'text') {
        return processing(message[0].text);
      }

      const parseMessage = message.map((frg, index) => {
        switch (frg.type) {
          case 'image_url':
            return (
              <div
                key={index}
                className={`max-h-80 w-full flex justify-center items-center`}
              >
                <div
                  className={`max-h-80 h-fit cursor-pointer relative ${
                    !frg.loading ? 'w-fit' : 'w-full'
                  }`}
                  onClick={() => onOpenFull(getImgTag(frg, true))}
                >
                  <div
                    className={`max-h-80 h-fit cursor-pointer relative ${
                      !frg.loading ? 'w-fit' : 'w-full'
                    }`}
                    onClick={() => onOpenFull(getImgTag(frg, true))}
                  >
                    {frg.loading ? (
                      <div
                        className={`rounded-[14px] w-full h-80 flex justify-center items-center  ${
                          theme == 'dark' ? 'bg-ship-grey' : 'bg-lavender'
                        }`}
                      >
                        <img
                          className="h-9"
                          key={Math.random()}
                          src="/gifs/straico_loader.gif"
                          alt="img ref"
                        />
                      </div>
                    ) : (
                      getImgTag(frg)
                    )}
                    {frg.no_actions || userType == 'user' ? (
                      ''
                    ) : (
                      <div className="absolute p-2 bottom-0 right-0 rounded-b-[14px] w-full bg-gradient-to-t from-raisin-black to-transparent">
                        <div className="relative flex gap-2 justify-end">
                          {/* Ícono de descarga */}
                          <Tooltip
                            title="Download image"
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  fontSize: '14px', // o '16px', '1rem', etc.
                                  fontFamily: 'Figtree', // opcional para mantener tu estilo
                                },
                              },
                            }}
                          >
                            <ArrowDownTrayIcon
                              onClick={(e) =>
                                handleDownload(e, frg.image_url.url)
                              }
                              className="w-5 h-5 font-bold text-white cursor-pointer"
                            />
                          </Tooltip>

                          {/* Ícono de favoritos */}
                          {onFavoriteFile && (
                            <Tooltip
                              title="Add image to your attachments"
                              arrow
                              placement="top"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: '14px',
                                    fontFamily: 'Figtree',
                                  },
                                },
                              }}
                            >
                              <HeartIcon
                                id={'favorite' + frg._id}
                                onClick={(e) => handleFavorite(e, frg)}
                                className={`w-5 h-5 font-bold ${
                                  frg.favorite ? 'text-nue-blue' : 'text-white'
                                } cursor-pointer`}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          default:
            return processing(frg.text);
        }
      });
      return parseMessage;
    };

    const getImgTag = (frg, maxScreen = false) => {
      return (
        <img
          className={`max-h-${
            maxScreen ? '[calc(100vh_-_2rem)]' : '80'
          } rounded-[14px]`}
          key={Math.random()}
          src={frg.image_url.url}
          alt="img ref"
        />
      );
    };

    const handleDownload = (e, url) => {
      try {
        // Extraer el nombre del archivo de la URL
        const fileName = url.substring(url.lastIndexOf('/') + 1) || 'image.jpg';

        // Crear un elemento de enlace temporal
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank'; // Corregir el valor de target

        // Agregar el enlace al DOM, hacer clic y luego eliminarlo
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Notificar al usuario que la descarga ha comenzado
        toast.success('Download started');
      } catch (error) {
        console.error('Error to download image:', error);
        toast.error('Error to download image');
      }
    };

    const handleDownloadVideo = async (e, url) => {
      try {
        // If an event is passed, stop the propagation
        if (e && e.stopPropagation) {
          e.stopPropagation();
        }

        const token = localStorage.getItem('token');
        if (!token) {
          toast.error('No token found');
          return;
        }

        const response = await fetch(
          `/api/file/download?url=${encodeURIComponent(url)}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to download file');
        }

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = url.substring(url.lastIndexOf('/') + 1) || 'video.mp4';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Download started');
      } catch (error) {
        console.error('Error downloading file:', error);
        toast.error('Error downloading file');
      }
    };

    const handleFavorite = (e, item) => {
      e.stopPropagation();

      try {
        if (!item.favorite) {
          const url = item.image_url.url;
          const fileName = url.substring(url.lastIndexOf('/') + 1);

          // Llamar a la función de favoritos con los datos necesarios
          onFavoriteFile({
            url: url,
            name: fileName,
            size: null,
            enabled: true,
            type: 'image',
            imageId: item._id,
            chatId: selectedChat.chat._id,
          });

          // Actualizar la UI para reflejar que el elemento es favorito
          const heartIcon = document.getElementById('favorite' + item._id);
          if (heartIcon) {
            heartIcon.classList.remove('text-white');
            heartIcon.classList.add('text-nue-blue');
            item.favorite = true;
          }

          toast.success('Image added to favorites');
        } else {
          toast.error('The file is already in your attachments.');
        }
      } catch (error) {
        console.error('Error to add to favorites:', error);
        toast.error('Error to add to favorites');
      }
    };

    const customStylesRegex = /""'\s*([^']*)\s*'""/g;

    const regex_1 = /```"'(.*?)'"```/g;
    const regex_2 = /```""'(.*?)'""```/g;

    const processing = (text) => {
      let formattedMessage = text
        .replace(regex_1, (match) => {
          return `""'${match.replaceAll('"', '').replaceAll("'", '')}'""`;
        })
        .replace(regex_2, (match) => {
          return `""'${match.replaceAll('"', '').replaceAll("'", '')}'""`;
        });

      if (userType === 'user') {
        formattedMessage = formattedMessage.replaceAll(
          customStylesRegex,
          (match) => {
            return `<span id='template'>${match
              .replaceAll('"', '')
              .replaceAll("'", '')}</span>`;
          }
        );
      }

      // Render mathematical equations detected by their delimiters
      formattedMessage = formattedMessage
        .replace(/\\\[([^\]]+?)\\\]/g, (_, equation) =>
          renderEquation(equation, true)
        )
        .replace(/\\\(([^\)]+?)\\\)/g, (_, equation) =>
          renderEquation(equation, false)
        )
        .replace(/\\\[([^\]]+?)\\\)/g, (_, equation) =>
          renderEquation(equation, true)
        )
        .replace(/\\\(([^\)]+?)\\\]/g, (_, equation) =>
          renderEquation(equation, false)
        );

      return formattedMessage;
    };

    const indentBulletPoints = (content) => {
      const lines = content.split('\n');
      for (let i = 0; i < lines.length; i++) {
        if (/^\d+\.\s/.test(lines[i])) {
          for (let j = i + 1; j < lines.length; j++) {
            if (/^-/.test(lines[j])) {
              lines[j] = '   ' + lines[j];
            } else {
              break;
            }
          }
        }
      }
      return lines.join('\n');
    };

    const renderMessageContent = (message) => {
      if (!message) {
        return;
      }
      const messageContent = getMessage(message);

      let processedMessageContent = messageContent;

      const processedContent = processTextWithHighlights(
        processedMessageContent,
        highlights.length ? highlights : newHighlights
      );

      if (typeof processedMessageContent === 'string') {
        // bullet points indentation
        processedMessageContent = indentBulletPoints(processedContent);
      }

      if (typeof processedMessageContent === 'string') {
        return (
          <MemoizedReactMarkdown
            className="prose break-words dark:prose-invert prose-p:leading-relaxed markdown prose-pre:p-0 text-base"
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeRaw]}
            components={MarkdownComponents}
          >
            {processedMessageContent}
          </MemoizedReactMarkdown>
        );
      } else {
        return (
          <div
            className={`py-2 grid gap-2 justify-items-center ${
              processedMessageContent.length > 1 && 'grid-cols-2'
            }`}
          >
            {processedMessageContent}
          </div>
        );
      }
    };

    const closeModal = () => {
      setModalImage(null);
      setIsOpen(false);
    };

    const closeMaskEditor = () => {
      setMaskEditorImage(null);
      setMaskEditorOpen(false);
    };

    const [processingImages, setProcessingImages] = useState(new Set());

    const getImageState = (func, msg) => {
      const savedCapabilities = localStorage.getItem('capabilities');
      const localCapabilities = savedCapabilities
        ? JSON.parse(savedCapabilities)
        : [];

      if (!localCapabilities || localCapabilities.length === 0) {
        return null;
      }

      if (func && !msg) {
        if (loading && capability?.name !== 'Browsing') {
          return (
            <div
              className={`rounded-[14px] w-full h-80 flex justify-center items-center relative overflow-hidden ${
                theme == 'dark' ? 'bg-ship-grey' : 'bg-lavender'
              }`}
              style={{
                background: `linear-gradient(
                  to right,
                  ${theme === 'dark' ? '#333333' : '#e5e7eb'} 0%,
                  ${theme === 'dark' ? '#555555' : '#ffffff'} 50%,
                  ${theme === 'dark' ? '#333333' : '#e5e7eb'} 100%
                )`,
                backgroundSize: '200% 100%',
                animation: 'shimmer 2s infinite',
              }}
            />
          );
        }
      }

      return null;
    };

    const renderImages = () => {
      // Función para manejar la adición de imágenes a favoritos
      const handleFavoriteImage = async (e, image) => {
        e.stopPropagation();

        if (processingImages.has(image.url)) return;

        try {
          if (!image.favorite) {
            // Marcar la imagen como en proceso
            setProcessingImages((prev) => new Set(prev).add(image.url));

            // Llamar a la función de favoritos
            onFavoriteFile({
              url: image.url,
              name: image.url.substring(image.url.lastIndexOf('/') + 1),
              size: null,
              enabled: true,
              type: 'image',
              chatId: selectedChat.chat._id,
            });

            // Actualizar el estado de la imagen
            image.favorite = true;

            // Eliminar la imagen del conjunto de procesamiento
            setProcessingImages((prev) => {
              const updated = new Set(prev);
              updated.delete(image.url);
              return updated;
            });

            toast.success('Image added to favorites');
          } else {
            toast.error('The file is already in your attachments.');
          }
        } catch (error) {
          console.error('Error to add image to favorites:', error);
          toast.error('Error to add image to favorites');

          // Asegurarse de eliminar la imagen del conjunto de procesamiento en caso de error
          setProcessingImages((prev) => {
            const updated = new Set(prev);
            updated.delete(image.url);
            return updated;
          });
        }
      };

      // Mostrar estado de carga si es necesario
      if (!images && loading && capability) {
        return (
          <div
            className={`rounded-[14px] w-full h-80 flex justify-center items-center relative overflow-hidden ${
              theme == 'dark' ? 'bg-ship-grey' : 'bg-lavender'
            }`}
            style={{
              background: `linear-gradient(
                to right,
                ${theme === 'dark' ? '#333333' : '#e5e7eb'} 0%,
                ${theme === 'dark' ? '#555555' : '#ffffff'} 50%,
                ${theme === 'dark' ? '#333333' : '#e5e7eb'} 100%
              )`,
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite',
            }}
          ></div>
        );
      }

      // Renderizar imágenes si existen
      if (images && images.length > 0) {
        return (
          <div className="gap-10 max-w-sm mx-auto">
            {images.map((image, index) => (
              <div
                key={index}
                className="relative group max-h-90 w-full flex justify-center items-center"
              >
                <img
                  src={image.url}
                  alt={`Image ${index + 1}`}
                  className="max-h-90 rounded-[14px] cursor-pointer w-full object-cover"
                  onClick={() => {
                    setModalImage(image.url);
                    setIsOpen(true);
                  }}
                  loading="lazy"
                />

                {!renderedFromShareChatPage && (
                  <div className="absolute bottom-2 right-2 flex gap-2">
                    <Tooltip title="Descargar imagen" placement="top" arrow>
                      <button
                        onClick={(e) => handleDownload(e, image.url)}
                        className="bg-black bg-opacity-50 p-1 rounded-md text-white"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5" />
                      </button>
                    </Tooltip>

                    {onFavoriteFile && (
                      <Tooltip
                        title="Agregar a favoritos"
                        placement="top"
                        arrow
                      >
                        <button
                          id={`favorite-${image.url}`}
                          onClick={(e) => handleFavoriteImage(e, image)}
                          className={`bg-black bg-opacity-50 p-1 rounded-md ${
                            image.favorite ? 'text-nue-blue' : 'text-white'
                          }`}
                          disabled={processingImages.has(image.url)}
                        >
                          <HeartIcon className="w-5 h-5 font-bold" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      return null;
    };

    const renderVideos = () => {
      if (!videos && loading && capability) {
        return (
          <div
            className={`rounded-[14px] w-full h-80 flex justify-center items-center relative overflow-hidden ${
              theme == 'dark' ? 'bg-ship-grey' : 'bg-lavender'
            }`}
            style={{
              background: `linear-gradient(
                to right,
                ${theme === 'dark' ? '#333333' : '#e5e7eb'} 0%,
                ${theme === 'dark' ? '#555555' : '#ffffff'} 50%,
                ${theme === 'dark' ? '#333333' : '#e5e7eb'} 100%
              )`,
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s infinite',
            }}
          ></div>
        );
      }

      if (videos && videos.length > 0) {
        return (
          <div className="relative group w-full flex justify-center items-center">
            <video
              controls
              className="w-auto h-auto max-w-full rounded-[14px]"
              preload="metadata"
            >
              <source src={videos} type="video/mp4" />
            </video>
            {!renderedFromShareChatPage && (
              <div className="absolute top-2 right-2 flex gap-2">
                <Tooltip title="Download video" placement="top" arrow>
                  <button
                    onClick={(e) => handleDownloadVideo(e, videos)}
                    className="bg-black bg-opacity-50 p-1 rounded-md text-white"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5" />
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      }

      return null;
    };

    /*** Highlight behaviour block  */ /////////////////////////////////////////////////
    const {
      handleTouchEnd,
      handleTouchMove,
      handleTouchStart,
      handleTextSelection,
      mobileSelectionAllowed,
      processTextWithHighlights,
    } = useHighlight({
      message,
      messageId,
      getMessage,
      isMobile,
      expanded,
      mergeActive,
      mergeState,
      setNewHighlights,
      newHighlights,
      allHighlight,
      setAllHighlight,
      touchStartPos,
      setTouchStartPos,
      isTouching,
      setIsTouching,
      highlight,
      messageContainerRef,
      messages,
      ref,
    });
    /*** End highlight behaviour block  */ /////////////////////////////////////////////

    return (
      <div className={`${!expanded && 'line-clamp-2'}`}>
        <Modal
          isOpen={isOpen}
          onRequestClose={closeModal}
          contentLabel="Image Modal"
          className="flex justify-center items-center"
          overlayClassName="fixed inset-0 bg-black bg-opacity-70 z-[9999]"
          style={{
            content: {
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              border: 'none',
              background: 'transparent',
              padding: '0',
              maxWidth: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
            },
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 9999,
            },
          }}
        >
          {modalImage && (
            <img
              src={modalImage}
              alt="Full view"
              className="max-w-full max-h-[90vh] rounded-md"
            />
          )}
        </Modal>

        <ImageMaskEditorModal
          isOpen={maskEditorOpen}
          onRequestClose={closeMaskEditor}
          imageUrl={maskEditorImage}
          store={store}
          onSend={onSend}
        />

        {userType === 'user' ? (
          <>
            <MemoizedOriginalPrompt
              originalPrompt={originalPrompt}
              theme={theme}
              optimizing={optimizing}
            />
            <HighlightedText text={getMessage(message)} />
          </>
        ) : (
          <>
            <div
              ref={messageContainerRef}
              onMouseUp={handleTextSelection}
              onTouchStart={mobileSelectionAllowed ? handleTouchStart : null}
              onTouchMove={mobileSelectionAllowed ? handleTouchMove : null}
              onTouchEnd={mobileSelectionAllowed ? handleTouchEnd : null}
              className={`touch-manipulation ${
                mobileSelectionAllowed && 'select-text'
              }`}
            >
              {!(
                mergeActive &&
                mergeState === MERGE_STATES.HIGHLIGHT &&
                highlight
              ) && (
                <MetaContent
                  loading={loading}
                  reasoning={reasoning}
                  annotations={annotations}
                />
              )}
              {renderMessageContent(message)}
            </div>
            {renderImages()}
            {getImageState(functionCapability, message)}
            {renderVideos()}
          </>
        )}
      </div>
    );
  }
);

export default memo(ContentMessage);
